/**
 * @file Wishlist.js - Public field-recording wishlist / scavenger hunt.
 *
 * Unauthenticated by design: contributors in the field (on a boat, with
 * no account and often no signal) fill this out via a shared link, not
 * the site nav. Checking a box logs a recording and adds a persistent
 * green tally mark -- but the checkbox itself is local-only UI state,
 * not saved, so unchecking and checking again logs another recording.
 * Multiple people recording the same item is the point.
 */

import React, { useEffect, useState } from 'react';
import config from '../config/config';

const adminServerBaseURL = config.adminServer.baseURL;

const MAX_VISIBLE_TALLIES = 6;

const TallyMarks = ({ count }) => {
  if (count <= MAX_VISIBLE_TALLIES) {
    return (
      <span className="tally-marks">
        {Array.from({ length: count }).map((_, i) => (
          <span key={i} className="tally-mark" role="img" aria-label="recorded">✅</span>
        ))}
      </span>
    );
  }
  return (
    <span className="tally-marks">
      <span className="tally-mark" role="img" aria-label="recorded">✅</span>
      <span className="tally-overflow">&times;{count}</span>
    </span>
  );
};

const Wishlist = () => {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [justTapped, setJustTapped] = useState(null);
  // Local-only, ephemeral: which boxes are checked this visit. Never sent
  // to the server and never persisted — resets on reload by design. The
  // green tally marks (from item.tallyCount) are the persistent record.
  const [checkedIds, setCheckedIds] = useState(new Set());

  useEffect(() => {
    fetch(`${adminServerBaseURL}/api/wishlist/list`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    })
      .then(r => r.json())
      .then(data => {
        setItems(data.items || []);
        setIsLoading(false);
      })
      .catch(() => {
        setError('Could not load the wishlist. Try reloading.');
        setIsLoading(false);
      });
  }, []);

  const handleCheck = (wishlistID, isChecked) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (isChecked) next.add(wishlistID); else next.delete(wishlistID);
      return next;
    });
    // Only checking (not unchecking) logs a recording — unchecking is just
    // clearing your own local mark, not retracting a tally.
    if (isChecked) handleTally(wishlistID);
  };

  const handleTally = (wishlistID) => {
    // Optimistic bump so the tap feels instant even on weak signal.
    setItems(prev => prev.map(item =>
      item.wishlistID === wishlistID ? { ...item, tallyCount: item.tallyCount + 1 } : item
    ));
    setJustTapped(wishlistID);
    setTimeout(() => setJustTapped(null), 350);

    fetch(`${adminServerBaseURL}/api/wishlist/tally`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ wishlistID }),
    })
      .then(r => r.json())
      .then(data => {
        if (typeof data.tallyCount === 'number') {
          setItems(prev => prev.map(item =>
            item.wishlistID === wishlistID ? { ...item, tallyCount: data.tallyCount } : item
          ));
        }
      })
      .catch(() => {
        // Optimistic count stands for this visit even if the write failed.
      });
  };

  const grandTotal = items.reduce((sum, item) => sum + item.tallyCount, 0);

  // Items arrive pre-sorted by sortOrder, which clusters categories together.
  const categories = [];
  items.forEach(item => {
    const last = categories[categories.length - 1];
    if (last && last.name === item.category) {
      last.items.push(item);
    } else {
      categories.push({ name: item.category, items: [item] });
    }
  });

  return (
    <div className="profile-edit-wrapper">
      <div className="homepage-box-wrapper">
        <div className="homepage-box wishlist-box">

          <div className="wishlist-header">
            <h2 className="title">River Audio Scavenger Hunt</h2>
            <p>
              I'm building <strong>Voices of River History</strong>, a live, ever-changing
              broadcast out of real river sound — currents, storms, dock creaks, overheard
              voices, the whole racket of actually being out there. Right now the library's
              full of stock audio standing in for the real thing. You're floating down the
              actual Mississippi. This is a scavenger hunt for your ears — bag as many of
              these as you can, the weirder the better.
            </p>
            <p>
              Whenever you catch one, hit record for the length noted, then check it off.
              <strong> Multiple people catching the same sound is a good thing</strong> —
              every boat, every bend in the river sounds different. Check it again any time
              you record another one, even if someone already has.
            </p>
            {!isLoading && !error && (
              <p className="wishlist-total">
                <span className="wishlist-total-n">{grandTotal}</span> recordings logged by river punks
              </p>
            )}
            <div className="wishlist-send">
              <p className="wishlist-send-title">Got something? Send it my way — raw file, no need to trim:</p>
              <p>
                Email <a href="mailto:wes@peoplesriverhistory.us">wes@peoplesriverhistory.us</a>,
                {' '}or text or Signal it to <a href="sms:+18314190044">831-419-0044</a>.
              </p>
            </div>
            <p className="wishlist-thanks">Thank you for helping build this. — Wes</p>
          </div>

          {isLoading && <p>Loading the list…</p>}
          {error && <p className="form-error">{error}</p>}

          {categories.map(cat => (
            <div className="wishlist-category" key={cat.name}>
              <h3 className="title">{cat.name}</h3>
              <ul className="wishlist-items">
                {cat.items.map(item => (
                  <li className="wishlist-item" key={item.wishlistID}>
                    <label className="wishlist-check-label">
                      <input
                        type="checkbox"
                        className="wishlist-checkbox"
                        checked={checkedIds.has(item.wishlistID)}
                        onChange={(e) => handleCheck(item.wishlistID, e.target.checked)}
                      />
                      <span className="wishlist-item-desc">
                        {item.description}{' '}
                        <span className="wishlist-duration">({item.durationNote})</span>
                      </span>
                    </label>
                    <span className={`wishlist-tally-display${justTapped === item.wishlistID ? ' just-tapped' : ''}`}>
                      <TallyMarks count={item.tallyCount} />
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}

        </div>
      </div>
    </div>
  );
};

export default Wishlist;
